import {
  type ReactNode,
  type ReactElement,
  forwardRef,
  type ComponentPropsWithoutRef,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Code text variants using class-variance-authority
 */
const codeTextVariants = cva('font-mono', {
  variants: {
    size: {
      sm: 'text-sm',
      xs: 'text-xs',
    },
    display: {
      inline: '',
      block: '',
    },
    background: {
      true: 'bg-slate-100 dark:bg-slate-800',
      false: '',
    },
  },
  compoundVariants: [
    {
      display: 'inline',
      background: true,
      class: 'px-1 rounded',
    },
    {
      display: 'block',
      background: true,
      class: 'p-3 rounded-md',
    },
  ],
  defaultVariants: {
    size: 'sm',
    display: 'inline',
    background: false,
  },
});

export interface CodeTextProps
  extends
    ComponentPropsWithoutRef<'code'>,
    VariantProps<typeof codeTextVariants> {
  /**
   * The text size
   * @default 'sm'
   */
  size?: 'xs' | 'sm';

  /**
   * The display type (inline or block)
   * - inline: Code that flows with text (use in paragraphs)
   * - block: Standalone code block (use with HighlightBox for container styling)
   * @default 'inline'
   */
  display?: 'inline' | 'block';

  /**
   * Whether to apply background styling
   * - For inline: adds px-1 rounded (background with padding)
   * - For block: adds p-3 rounded-md (background with padding)
   * @default false
   */
  background?: boolean;

  /**
   * The semantic HTML element to render (code, pre, span, div)
   * Defaults to 'code' for inline, 'pre' for block
   * @default 'code'
   */
  as?: 'code' | 'pre' | 'span' | 'div';

  /**
   * The content to display
   */
  children: ReactNode;
}

/**
 * Base CodeText component with variant support
 */
const CodeText = forwardRef<HTMLElement, CodeTextProps>(
  (
    {
      size = 'sm',
      display = 'inline',
      background = false,
      as,
      children,
      className,
      ...props
    },
    ref
  ): ReactElement => {
    const defaultAs = display === 'block' ? 'pre' : 'code';
    const Component = as || defaultAs;

    return (
      <Component
        // @ts-expect-error - Polymorphic component ref typing limitation: ref type varies by 'as' prop
        ref={ref}
        className={cn(
          codeTextVariants({
            size,
            display,
            background,
          }),
          className
        )}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

CodeText.displayName = 'CodeText';

/**
 * Code - Inline code text
 * @example
 * ```tsx
 * <Code>const variable = "value"</Code>
 * <Code background>inline code with background</Code>
 * ```
 */
export const Code = forwardRef<
  HTMLElement,
  Omit<CodeTextProps, 'size' | 'display'> & { background?: boolean }
>(({ as = 'code', background = false, ...props }, ref) => (
  <CodeText
    ref={ref}
    size="sm"
    display="inline"
    background={background}
    as={as}
    {...props}
  />
));
Code.displayName = 'Code';

/**
 * CodeSmall - Small inline code text
 * @example
 * ```tsx
 * <CodeSmall>small code</CodeSmall>
 * <CodeSmall background>small code with background</CodeSmall>
 * ```
 */
export const CodeSmall = forwardRef<
  HTMLElement,
  Omit<CodeTextProps, 'size' | 'display'> & { background?: boolean }
>(({ as = 'code', background = false, ...props }, ref) => (
  <CodeText
    ref={ref}
    size="xs"
    display="inline"
    background={background}
    as={as}
    {...props}
  />
));
CodeSmall.displayName = 'CodeSmall';

/**
 * CodeBlock - Block code (use with HighlightBox for container styling)
 * @example
 * ```tsx
 * // Standalone block code
 * <CodeBlock background>
 *   const code = "block";
 *   console.log(code);
 * </CodeBlock>
 *
 * // Block code with HighlightBox container
 * <HighlightBox variant="neutral">
 *   <CodeBlock>const code = "block";</CodeBlock>
 * </HighlightBox>
 * ```
 */
export const CodeBlock = forwardRef<
  HTMLElement,
  Omit<CodeTextProps, 'size' | 'display'> & { background?: boolean }
>(({ as = 'pre', background = false, ...props }, ref) => (
  <CodeText
    ref={ref}
    size="sm"
    display="block"
    background={background}
    as={as}
    {...props}
  />
));
CodeBlock.displayName = 'CodeBlock';
